import React, { useRef, useEffect } from "react";
import { useSectionInView } from "../assets/lib/hooks";
import { useTheme } from "../context/theme-context";
import { motion, useScroll, useTransform } from "framer-motion";
import * as d3 from "d3";
import { useAppDispatch, useTypedSelector } from '../stateStore';

const Experience: React.FC = () => {

  const experienceStatus = useTypedSelector(state => state.Experience.loading)
  const experienceData = useTypedSelector(state => state.Experience.data)

  // const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const { ref } = useSectionInView("Experience");
  const { theme } = useTheme();
  const animationReference = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: animationReference,
    offset: ["0 1", "1.33 1"],
  });
  const scaleProgess = useTransform(scrollYProgress, [0, 1], [0.8, 1]);
  const opacityProgess = useTransform(scrollYProgress, [0, 1], [0.6, 1]);

  let screenWidth = [600, 800, 1000, 1200, 1500, 1800]

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', function (event) {
      handleResize()
    });
  }, [experienceStatus, experienceData, theme]);

  function handleResize() {
    if (!experienceStatus) {
      draw(window.document.body.clientWidth)
    }
  }

  function draw(clientWidth: any) {
    const margin = { t: 30, r: 20, b: 20, l: 40 },
      w = 400 - margin.l - margin.r,
      h = 550 - margin.t - margin.b,
      x = d3.scaleLinear().range([0, w]),
      y = d3.scaleLinear().range([0, h - 60]),
      color = d3.scaleOrdinal(d3.schemeCategory10);

    d3.selectAll("#experienceplotDiv").remove()

    const svg = d3.select("#experience")
      .append("div")
      .attr("style", "")
      .attr("id", "experienceplotDiv")
      .append("svg")
      .attr("preserveAspectRatio", "xMinYMin meet")
      .classed("svg-content-responsive", true);

    switch (true) {
      case (clientWidth < screenWidth[0]):
        svg.attr("viewBox", "0 0 " + 400 + " " + 500)
        break;
      case (clientWidth < screenWidth[1]):
        svg.attr("viewBox", "-100 0 " + 600 + " " + 500)
        break;
      case (clientWidth < screenWidth[2]):
        svg.attr("viewBox", "-200 0 " + 800 + " " + 500)
        break;
      case (clientWidth < screenWidth[3]):
        svg.attr("viewBox", "-250 0 " + 900 + " " + 500)
        break;
      case (clientWidth < screenWidth[4]):
        svg.attr("viewBox", "-300 0 " + 1000 + " " + 500)
        break;
      case (clientWidth < screenWidth[5]):
        svg.attr("viewBox", "-350 0 " + 1100 + " " + 500)
        break;
      default:
        svg.attr("viewBox", "-400 0 " + 1200 + " " + 500)
        break;
    }

    const axisLine = d3.line()
      .x(function (d: any) { return d.x_axis; })
      .y(function (d: any) { return d.y_axis; })
      .curve(d3.curveLinear);

    svg.append("path")
      .attr("d", axisLine(experienceData.axis.path.lines))
      .attr("stroke", experienceData.axis.path[theme + "Stroke"])
      .attr("stroke-width", experienceData.axis.path.strokeWidth)
      .attr("fill", experienceData.axis.path[theme + "Fill"]);
    svg.append("path")
      .attr("d", axisLine(experienceData.axis.path.lineEnd))
      .attr("stroke", experienceData.axis.path[theme + "Stroke"])
      .attr("stroke-width", experienceData.axis.path.strokeWidth)
      .attr("fill", experienceData.axis.path[theme + "Fill"])
      .style("stroke-dasharray", experienceData.axis.path.strokedasharray);

    const pointerLine = d3.line()
      .x(function (d: any) { return d.x_axis; })
      .y(function (d: any) { return d.y_axis; })
      .curve(d3.curveLinear);

    experienceData.experience.forEach(function (element: any) {
      if (element.path) {
        svg.append("path")
          .attr("d", pointerLine(element.path.lines))
          .attr("stroke", element.path[theme + "Stroke"])
          .attr("stroke-width", element.path.strokeWidth)
          .attr("fill", element.path[theme + "Fill"])
      }
    });

    const items = svg.selectAll("g")
      .data(experienceData.experience)
      .enter()
      .append("g");

    items.append("text")
      .attr("x", function (d: any) { return d.labelYear.cx; })
      .attr("y", function (d: any) { return d.labelYear.cy; })
      .text(function (d: any) { return d.labelYear.text; })
      .attr("font-family", function (d: any) { return d.labelYear.fontFamily; })
      .attr("font-size", function (d: any) { return d.labelYear.fontSize; })
      .attr("fill", function (d: any) { return d.labelYear[theme + "Fill"]; })
    items.append("text")
      .attr("x", function (d: any) { return d.labelDesc1.cx; })
      .attr("y", function (d: any) { return d.labelDesc1.cy; })
      .text(function (d: any) { return d.labelDesc1.text; })
      .attr("font-family", function (d: any) { return d.labelDesc1.fontFamily; })
      .attr("font-size", function (d: any) { return d.labelDesc1.fontSize; })
      .attr("fill", function (d: any) { return d.labelDesc1[theme + "Fill"]; })
    items.append("text")
      .attr("x", function (d: any) { return d.labelDesc2.cx; })
      .attr("y", function (d: any) { return d.labelDesc2.cy; })
      .text(function (d: any) { return d.labelDesc2.text; })
      .attr("font-family", function (d: any) { return d.labelDesc2.fontFamily; })
      .attr("font-size", function (d: any) { return d.labelDesc2.fontSize; })
      .attr("fill", function (d: any) { return d.labelDesc2[theme + "Fill"]; })

    items.append("circle")
      .attr("cx", function (d: any) { return d.outterCircle.x_axis; })
      .attr("cy", function (d: any) { return d.outterCircle.y_axis; })
      .attr("r", function (d: any) { return d.outterCircle.radius; })
      .attr("stroke", function (d: any) { return d.outterCircle[theme + "Stroke"]; })
      .attr("stroke-width", function (d: any) { return d.outterCircle.width; })
      .style("fill", function (d: any) { return d.outterCircle[theme + "Fill"]; });
    items.append("circle")
      .attr("cx", function (d: any) { return d.innerCircle.x_axis; })
      .attr("cy", function (d: any) { return d.innerCircle.y_axis; })
      .attr("r", function (d: any) { return d.innerCircle.radius; })
      .attr("stroke", function (d: any) { return d.innerCircle[theme + "Stroke"]; })
      .attr("stroke-width", function (d: any) { return d.innerCircle.width; })
      .style("fill", function (d: any) { return d.innerCircle[theme + "Fill"]; });

    experienceData.legend.forEach(function (element: any) {
      svg.append("text")
        .attr("x", element.label.cx)
        .attr("y", element.label.cy)
        .text(element.label.text)
        .attr("font-family", element.label.fontFamily)
        .attr("font-size", element.label.fontSize)
        .attr("fill", element.label[theme + "Fill"]);
      svg.append("circle")
        .attr("cx", element.outterCircle.x_axis)
        .attr("cy", element.outterCircle.y_axis)
        .attr("r", element.outterCircle.radius)
        .attr("stroke", element.outterCircle[theme + "Stroke"])
        .attr("stroke-width", element.outterCircle.width)
        .style("fill", element.outterCircle[theme + "Fill"]);
      svg.append("circle")
        .attr("cx", element.innerCircle.x_axis)
        .attr("cy", element.innerCircle.y_axis)
        .attr("r", element.innerCircle.radius)
        .attr("stroke", element.innerCircle[theme + "Stroke"])
        .attr("stroke-width", element.innerCircle.width)
        .style("fill", element.innerCircle[theme + "Fill"]);
    });
  }

  return (
    <React.Fragment>
      <section
        className="relative tech-stack"
        id="experience"
        ref={ref}
      >

        <div className="title-container flex flex-col justify-center items-center p-32 gap-6">
          <motion.div
            ref={animationReference}
            style={{
              scale: scaleProgess,
              opacity: opacityProgess,
            }}
          >
            <p className="font-black mb-6">
              <span className="text-[--orange]">&lt;</span>Experience
              <span className="text-[--orange]">/&gt;</span>
            </p>
            <h2>
              My Experience
            </h2>
          </motion.div>
        </div>
        <div id="experience"></div>
      </section>
    </React.Fragment>
  );
};

export default Experience;